import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Avatar,
  Chip,
  Rating,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
} from '@mui/material';
import {
  VideoCall as VideoCallIcon,
  Phone as PhoneIcon,
  Message as MessageIcon,
  Star as StarIcon,
  Schedule as ScheduleIcon,
  Computer as ComputerIcon,
  Smartphone as SmartphoneIcon,
  Tv as TvIcon,
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import expert1 from '../assets/expert1.jpg';
import expert2 from '../assets/expert2.jpg';
import expert3 from '../assets/expert3.jpg';
import logo0 from '../assets/logo0.png';
import Consult from '../assets/Consult.png';
import { useNavigate } from 'react-router-dom';

const Consultation = () => {
  const [selectedExpert, setSelectedExpert] = useState(null);
  const [bookingOpen, setBookingOpen] = useState(false);
  const [deviceType, setDeviceType] = useState('');
  const [issueDescription, setIssueDescription] = useState('');
  const navigate = useNavigate();

  const experts = [
    {
      id: 1,
      name: "Dr. Sarah Johnson",
      specialty: "Phone Repair Specialist",
      image: expert1,
      rating: 4.9,
      reviews: 128,
      experience: "8+ years",
      expertise: ["iOS", "Android", "Hardware"],
      availability: "Available Now",
      specialtyType: "phone"
    },
    {
      id: 2,
      name: "Michael Chen",
      specialty: "Computer Expert",
      image: expert2,
      rating: 4.8,
      reviews: 156,
      experience: "10+ years",
      expertise: ["PC", "Mac", "Networking"],
      availability: "Next Slot: 2PM",
      specialtyType: "computer"
    },
    {
      id: 3,
      name: "Emma Rodriguez",
      specialty: "TV & Display Specialist",
      image: expert3,
      rating: 4.7,
      reviews: 92,
      experience: "6+ years",
      expertise: ["Smart TV", "OLED", "LCD"],
      availability: "Available Now",
      specialtyType: "tv"
    },
  ];

  const getSpecialtyIcon = (type) => {
    switch(type) {
      case 'phone':
        return <SmartphoneIcon />;
      case 'computer':
        return <ComputerIcon />;
      case 'tv':
        return <TvIcon />;
      default:
        return null;
    }
  };

  return (
    <Box sx={{ bgcolor: '#000', minHeight: '100vh', color: 'white' }}>
      {/* Logo with color inversion */}
      <Box sx={{ 
        position: 'absolute', 
        top: 15, 
        left: 20, 
        zIndex: 1000,
      }}>
        <img 
          src={logo0} 
          alt="Warrili" 
          style={{ 
            height: '35px',
            filter: 'brightness(0) invert(1)',
            opacity: 0.9,
          }} 
        />
      </Box>

      {/* Hero Section with fixed image */}
      <Box sx={{
        position: 'relative',
        minHeight: '40vh',
        display: 'flex',
        alignItems: 'center',
        bgcolor: '#000',
        overflow: 'hidden',
      }}>
        {/* Background Image */}
        <Box sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          '& img': {
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            filter: 'brightness(0) invert(1) opacity(0.1)', // Inverts and adjusts opacity
          }
        }}>
          <img 
            src={Consult} 
            alt="Background"
          />
        </Box>

        {/* Content */}
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2, py: 4 }}>
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
              <Box sx={{ pt: 2 }}>
                <Typography variant="h3" sx={{
                  fontWeight: 600,
                  mb: 2,
                  color: 'white',
                  '& .highlight': {
                    color: '#FFE302',
                    position: 'relative',
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      left: 0,
                      bottom: -4,
                      width: '100%',
                      height: '2px',
                      backgroundColor: '#FFE302'
                    }
                  }
                }}>
                  Expert <span className="highlight">Video Consultation</span>
                </Typography>
                
                <Typography variant="h6" sx={{
                  mb: 3,
                  color: '#fff',
                  fontWeight: 400,
                }}>
                  Get real-time diagnosis and solutions from our certified experts
                </Typography>

                <Box sx={{ 
                  display: 'flex', 
                  gap: 2,
                  flexWrap: 'wrap',
                }}>
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 1,
                    color: '#FFE302'
                  }}>
                    <VideoCallIcon sx={{ fontSize: 20 }} />
                    <Typography variant="body2">Live Video Support</Typography>
                  </Box>
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 1,
                    color: '#FFE302'
                  }}>
                    <StarIcon sx={{ fontSize: 20 }} />
                    <Typography variant="body2">Certified Experts</Typography>
                  </Box>
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 1,
                    color: '#FFE302'
                  }}>
                    <CheckCircleIcon sx={{ fontSize: 20 }} />
                    <Typography variant="body2">Instant Solutions</Typography>
                  </Box>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Experts Section */}
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Typography variant="h4" sx={{ mb: 3 }}>
          Available Experts
        </Typography>
        <Grid container spacing={3}>
          {experts.map((expert) => (
            <Grid item xs={12} md={4} key={expert.id}>
              <Card sx={{
                bgcolor: '#111',
                color: 'white',
                border: '1px solid rgba(255,227,2,0.1)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-5px)',
                  borderColor: '#FFE302'
                }
              }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Avatar
                      src={expert.image}
                      sx={{ width: 80, height: 80, mr: 2 }}
                    />
                    <Box>
                      <Typography variant="h6" sx={{ mb: 0.5 }}>
                        {expert.name}
                      </Typography>
                      <Typography variant="body2" color="#FFE302" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {getSpecialtyIcon(expert.specialtyType)}
                        {expert.specialty}
                      </Typography>
                    </Box>
                  </Box>

                  <Box sx={{ mb: 2 }}>
                    <Rating value={expert.rating} precision={0.1} readOnly size="small" />
                    <Typography variant="body2" color="#999">
                      {expert.rating} ({expert.reviews} reviews)
                    </Typography>
                  </Box>

                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="#999">
                      Experience: {expert.experience}
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                      {expert.expertise.map((skill) => (
                        <Chip
                          key={skill}
                          label={skill}
                          size="small"
                          sx={{ bgcolor: 'rgba(255,227,2,0.1)', color: '#FFE302' }}
                        />
                      ))}
                    </Box>
                  </Box>

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Chip
                      icon={<ScheduleIcon />}
                      label={expert.availability}
                      color={expert.availability === "Available Now" ? "success" : "warning"}
                      size="small"
                    />
                    <Button
                      variant="contained"
                      startIcon={<VideoCallIcon />}
                      onClick={() => {
                        setSelectedExpert(expert);
                        setBookingOpen(true);
                      }}
                      sx={{
                        bgcolor: '#FFE302',
                        color: 'black',
                        '&:hover': {
                          bgcolor: '#e6cc00'
                        }
                      }}
                    >
                      Book Session
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Booking Dialog */}
      <Dialog 
        open={bookingOpen} 
        onClose={() => setBookingOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: '#111',
            color: 'white',
            borderRadius: 0
          }
        }}
      >
        <DialogTitle sx={{ borderBottom: '1px solid rgba(255,227,2,0.1)' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            Book Video Consultation
            <IconButton
              onClick={() => setBookingOpen(false)}
              sx={{ color: 'white' }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          {selectedExpert && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" sx={{ mb: 1 }}>
                Selected Expert:
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar src={selectedExpert.image} />
                <Box>
                  <Typography variant="h6">{selectedExpert.name}</Typography>
                  <Typography variant="body2" color="#FFE302">
                    {selectedExpert.specialty}
                  </Typography>
                </Box>
              </Box>
            </Box>
          )}

          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel sx={{ color: '#999' }}>Device Type</InputLabel>
            <Select
              value={deviceType}
              onChange={(e) => setDeviceType(e.target.value)}
              sx={{
                color: 'white',
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(255,227,2,0.1)'
                }
              }}
            >
              <MenuItem value="phone">Smartphone</MenuItem>
              <MenuItem value="computer">Computer/Laptop</MenuItem>
              <MenuItem value="tv">TV/Display</MenuItem>
            </Select>
          </FormControl>

          <TextField
            fullWidth
            multiline
            rows={4}
            label="Describe Your Issue"
            value={issueDescription}
            onChange={(e) => setIssueDescription(e.target.value)}
            sx={{
              mb: 3,
              '& .MuiOutlinedInput-root': {
                color: 'white',
                '& fieldset': {
                  borderColor: 'rgba(255,227,2,0.1)'
                }
              },
              '& .MuiInputLabel-root': {
                color: '#999'
              }
            }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 3, borderTop: '1px solid rgba(255,227,2,0.1)' }}>
          <Button
            onClick={() => setBookingOpen(false)}
            sx={{ color: '#999' }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            startIcon={<VideoCallIcon />}
            onClick={() => {
              const cleanExpert = {
                ...selectedExpert,
                icon: undefined
              };
              
              navigate('/bookconfirm', {
                state: {
                  expert: cleanExpert,
                  deviceType,
                  issueDescription
                }
              });
              setBookingOpen(false);
            }}
            sx={{
              bgcolor: '#FFE302',
              color: 'black',
              '&:hover': {
                bgcolor: '#e6cc00'
              }
            }}
          >
            Confirm Booking
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// Add these styles to your global CSS or styled-components
const globalStyles = `
  @keyframes float {
    0%, 100% { transform: translateY(0); }
    50% { transform: translateY(-10px); }
  }

  @keyframes pulse {
    0%, 100% { opacity: 0.5; transform: scale(1); }
    50% { opacity: 0.8; transform: scale(1.1); }
  }
`;

export default Consultation; 